import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Plus, Edit2, Trash2, X, Check, Users, Cloud, RefreshCw, Shield, Eye, Wrench, Search, Building2, Upload, Download, AlertCircle, Monitor, ExternalLink, ChevronDown, ChevronUp, Package, Cpu, KeyRound } from 'lucide-react'
import Papa from 'papaparse'

type Role = 'SUPER_ADMIN' | 'ADMIN' | 'VIEWER' | 'TECHNICIAN'

interface Company { id: string; name: string }
interface User {
  id: string
  username: string
  email?: string
  displayName?: string
  role: Role
  isActive: boolean
  o365Id?: string
  companyId?: string
  company?: Company
  employeeId?: string
  department?: string
  position?: string
  createdAt: string
}
interface O365User {
  o365Id: string; displayName: string; email: string
  jobTitle?: string; department?: string; alreadyImported: boolean
}

const ROLE_LABELS: Record<Role, string> = { SUPER_ADMIN: 'Super Admin', ADMIN: 'Admin', VIEWER: 'Viewer', TECHNICIAN: 'Technician' }
const ROLE_COLORS: Record<Role, string> = { SUPER_ADMIN: 'bg-purple-100 text-purple-700', ADMIN: 'bg-red-100 text-red-700', VIEWER: 'bg-blue-100 text-blue-700', TECHNICIAN: 'bg-green-100 text-green-700' }
const ROLE_ICONS: Record<Role, any> = { SUPER_ADMIN: Shield, ADMIN: Shield, VIEWER: Eye, TECHNICIAN: Wrench }
const ic = 'border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full'

function UserFormModal({ user, companies, isSuperAdmin, onClose, onSave }: {
  user?: User | null; companies: Company[]; isSuperAdmin?: boolean; onClose: () => void; onSave: (data: any) => void
}) {
  const [form, setForm] = useState({
    username:    user?.username    || '',
    displayName: user?.displayName || '',
    email:       user?.email       || '',
    role:        user?.role        || 'VIEWER',
    password:    '',
    isActive:    user?.isActive ?? true,
    companyId:   user?.companyId   || '',
    employeeId:  user?.employeeId  || '',
    department:  user?.department  || '',
    position:    user?.position    || '',
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: any = { ...form, companyId: form.companyId || null }
    if (!data.password) delete data.password
    onSave(data)
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">{user ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้ใหม่'}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Username *</label>
            <input value={form.username} onChange={set('username')} className={ic} placeholder="username" required/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">ชื่อ-นามสกุล</label>
            <input value={form.displayName} onChange={set('displayName')} className={ic} placeholder="ชื่อ-นามสกุล"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input value={form.email} onChange={set('email')} className={ic} placeholder="email@company.com" type="email"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">บริษัท</label>
            <select value={form.companyId} onChange={set('companyId')} className={ic}>
              <option value="">-- ไม่ระบุบริษัท --</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">สิทธิ์การใช้งาน</label>
            <select value={form.role} onChange={set('role')} className={ic}>
              <option value="VIEWER">Viewer — ดูข้อมูลได้อย่างเดียว</option>
              <option value="TECHNICIAN">Technician — รับมอบหมาย Ticket ได้</option>
              <option value="ADMIN">Admin — จัดการระบบทั้งหมด</option>
              {isSuperAdmin && <option value="SUPER_ADMIN">Super Admin — ควบคุมระบบทั้งหมดข้ามบริษัท</option>}
            </select>
            {form.role === 'TECHNICIAN' && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <Wrench size={11}/> Technician จะปรากฏใน dropdown ผู้รับผิดชอบ Ticket
              </p>
            )}
            {form.role === 'SUPER_ADMIN' && (
              <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                <Shield size={11}/> Super Admin ไม่ต้องผูกกับบริษัทใด — เห็นข้อมูลทุกบริษัท
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">แผนก/ฝ่าย</label>
              <input value={form.department} onChange={set('department')} className={ic} placeholder="เช่น IT Support"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">ตำแหน่ง</label>
              <input value={form.position} onChange={set('position')} className={ic} placeholder="เช่น System Admin"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">รหัสพนักงาน</label>
            <input value={form.employeeId} onChange={set('employeeId')} className={ic} placeholder="EMP0001"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {user ? 'รหัสผ่านใหม่ (เว้นว่างถ้าไม่เปลี่ยน)' : 'รหัสผ่าน *'}
            </label>
            <input value={form.password} onChange={set('password')} className={ic}
              placeholder={user ? 'เว้นว่างถ้าไม่เปลี่ยน' : 'รหัสผ่าน'} type="password" required={!user}/>
          </div>
          {user && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="isActive" checked={form.isActive}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600"/>
              <label htmlFor="isActive" className="text-sm text-gray-700">เปิดใช้งาน</label>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">ยกเลิก</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">บันทึก</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function O365ImportModal({ companies, onClose }: { companies: Company[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [companyId, setCompanyId] = useState('')

  const { data: o365Users = [], isLoading, error, refetch } = useQuery<O365User[]>({
    queryKey: ['o365-users'],
    queryFn: () => api.get('/users/o365').then(r => r.data),
    retry: false,
  })

  const importMut = useMutation({
    mutationFn: (users: O365User[]) => api.post('/users/o365-import', { users, companyId: companyId || null }),
    onSuccess: (res) => { alert(res.data.message); qc.invalidateQueries({ queryKey: ['users'] }); onClose() },
  })

  const filtered = o365Users.filter(u =>
    u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.department?.toLowerCase().includes(search.toLowerCase())
  )

  const toggleAll = () => {
    const selectable = filtered.filter(u => !u.alreadyImported).map(u => u.o365Id)
    if (selectable.every(id => selected.has(id))) {
      setSelected(s => { const n = new Set(s); selectable.forEach(id => n.delete(id)); return n })
    } else {
      setSelected(s => { const n = new Set(s); selectable.forEach(id => n.add(id)); return n })
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900">นำเข้าผู้ใช้จาก Microsoft 365</h2>
            <p className="text-xs text-gray-400 mt-0.5">เลือกผู้ใช้ที่ต้องการนำเข้าสู่ระบบ</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center p-10">
            <RefreshCw size={24} className="animate-spin text-blue-500"/>
            <span className="ml-3 text-gray-500">กำลังดึงข้อมูลจาก Microsoft 365...</span>
          </div>
        )}
        {error && (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <Cloud size={40} className="text-gray-300 mb-3"/>
            <p className="text-gray-600 font-medium">ไม่สามารถเชื่อมต่อ Microsoft 365 ได้</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">กรุณาตั้งค่า Azure AD ใน Settings ก่อน</p>
            <button onClick={() => refetch()} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">
              <RefreshCw size={14}/> ลองใหม่
            </button>
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="p-4 border-b border-gray-100 flex-shrink-0 space-y-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ค้นหาชื่อ, email, แผนก..."/>
                </div>
                <select value={companyId} onChange={e => setCompanyId(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="">-- ผูกกับบริษัท --</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline">
                  {filtered.filter(u => !u.alreadyImported).every(u => selected.has(u.o365Id)) ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                </button>
                <span className="text-xs text-gray-400">|</span>
                <span className="text-xs text-gray-500">เลือกแล้ว {selected.size} คน</span>
                <span className="text-xs text-gray-400 ml-auto">{filtered.length} รายการ</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
              {filtered.length === 0 && <div className="text-center text-gray-400 py-8 text-sm">ไม่พบผู้ใช้</div>}
              {filtered.map(u => (
                <div key={u.o365Id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    u.alreadyImported ? 'bg-gray-50 border-gray-100 opacity-60' :
                    selected.has(u.o365Id) ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-100 hover:border-blue-200 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (u.alreadyImported) return
                    setSelected(s => { const n = new Set(s); n.has(u.o365Id) ? n.delete(u.o365Id) : n.add(u.o365Id); return n })
                  }}>
                  <input type="checkbox" readOnly checked={u.alreadyImported || selected.has(u.o365Id)}
                    disabled={u.alreadyImported} className="rounded border-gray-300 text-blue-600 pointer-events-none"/>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {u.displayName?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.displayName}</p>
                    <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  </div>
                  {u.department && <span className="text-xs text-gray-400 hidden sm:block flex-shrink-0">{u.department}</span>}
                  {u.alreadyImported && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex-shrink-0">นำเข้าแล้ว</span>}
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
              <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">ยกเลิก</button>
              <button onClick={() => importMut.mutate(o365Users.filter(u => selected.has(u.o365Id)))}
                disabled={selected.size === 0 || importMut.isPending}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {importMut.isPending ? <RefreshCw size={14} className="animate-spin"/> : <Cloud size={14}/>}
                นำเข้า {selected.size > 0 ? `(${selected.size} คน)` : ''}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

interface AssetItem { id:string; assetCode?:string; name:string; type:string; status:string; brand?:string; model?:string; assignedTo?:string; employeeId?:string; company?:string; site?:string }
const STATUS_BADGE: Record<string,string> = { IN_USE:'bg-green-100 text-green-700', AVAILABLE:'bg-blue-100 text-blue-700', MAINTENANCE:'bg-yellow-100 text-yellow-700', RETIRED:'bg-gray-100 text-gray-500', LOST:'bg-red-100 text-red-700' }
const STATUS_TH: Record<string,string> = { IN_USE:'กำลังใช้', AVAILABLE:'ว่าง', MAINTENANCE:'ซ่อมบำรุง', RETIRED:'เลิกใช้', LOST:'สูญหาย' }

function AssetDetails({ assetId }: { assetId: string }) {
  const { data: software = [], isLoading: swLoading } = useQuery<any[]>({
    queryKey: ['asset-software', assetId],
    queryFn: () => api.get(`/software/asset/${assetId}`).then(r => r.data),
  })
  const { data: components = [], isLoading: compLoading } = useQuery<any[]>({
    queryKey: ['asset-components', assetId],
    queryFn: () => api.get(`/components?assetId=${assetId}`).then(r => r.data),
  })

  if (swLoading || compLoading) return <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-1"><RefreshCw size={11} className="animate-spin"/> กำลังโหลด...</div>

  const hasSoftware = software.length > 0
  const hasComponents = components.length > 0

  if (!hasSoftware && !hasComponents) return <p className="px-3 py-2 text-xs text-gray-400">— ไม่มี Software / Components</p>

  return (
    <div className="border-t border-gray-100 mt-2 pt-2 space-y-2">
      {hasSoftware && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1 mb-1 flex items-center gap-1"><Package size={10}/> Software ({software.length})</p>
          <div className="space-y-1">
            {software.map((sw: any) => (
              <div key={sw.id} className="flex items-center gap-2 px-2 py-1 bg-indigo-50 rounded-lg">
                <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${sw.softwareCategory==='OS'?'bg-indigo-200 text-indigo-700':'bg-gray-200 text-gray-600'}`}>
                  {sw.softwareCategory==='OS'?'OS':'App'}
                </span>
                <span className="text-xs text-gray-800 font-medium flex-1 truncate">{sw.name}</span>
                {sw.version && <span className="text-xs text-gray-400 flex-shrink-0">v{sw.version}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {hasComponents && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1 mb-1 flex items-center gap-1"><Cpu size={10}/> Components ({components.length})</p>
          <div className="space-y-1">
            {components.map((c: any) => {
              const TYPE_LABELS: Record<string,string> = { HDD:'HDD', SSD:'SSD', RAM:'RAM', BATTERY:'Battery', CPU:'CPU', GPU:'GPU', MOTHERBOARD:'Motherboard', PSU:'PSU', OTHER:'Other' }
              const COMP_STATUS: Record<string,string> = { ACTIVE:'bg-green-100 text-green-700', RETIRED:'bg-gray-100 text-gray-500', REPLACED:'bg-orange-100 text-orange-700' }
              const COMP_STATUS_TH: Record<string,string> = { ACTIVE:'ใช้งาน', RETIRED:'เลิกใช้', REPLACED:'เปลี่ยนแล้ว' }
              const label = TYPE_LABELS[c.type] || c.type
              const title = [c.brand, c.model].filter(Boolean).join(' ') || label
              const sub = [c.specs, c.serialNumber ? `S/N: ${c.serialNumber}` : ''].filter(Boolean).join(' · ')
              return (
                <div key={c.id} className="flex items-start gap-2 px-2 py-1.5 bg-amber-50 rounded-lg">
                  <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-amber-200 text-amber-700 flex-shrink-0 mt-0.5">{label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-800 font-medium truncate">{title}</p>
                    {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${COMP_STATUS[c.status] || 'bg-gray-100 text-gray-500'}`}>
                    {COMP_STATUS_TH[c.status] || c.status}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function UserAssetsModal({ user, onClose }: { user: User; onClose: () => void }) {
  const [expandedId, setExpandedId] = useState<string|null>(null)
  const { data, isLoading } = useQuery<{assets:AssetItem[];total:number}>({
    queryKey: ['user-assets', user.id],
    queryFn: async () => {
      // Search by displayName OR username, also by employeeId
      const name = user.displayName || user.username
      const params = new URLSearchParams({ assignedTo: name, limit: '100' })
      const res = await api.get(`/assets?${params}`).then(r => r.data)
      // Also fetch by employeeId if available, merge & deduplicate
      if (user.employeeId) {
        const res2 = await api.get(`/assets?employeeId=${user.employeeId}&limit=100`).then(r => r.data)
        const all = [...res.assets, ...res2.assets]
        const seen = new Set<string>()
        const merged = all.filter(a => { if (seen.has(a.id)) return false; seen.add(a.id); return true })
        return { assets: merged, total: merged.length }
      }
      return res
    },
  })
  const { data: licenses = [], isLoading: licLoading } = useQuery<any[]>({
    queryKey: ['user-licenses', user.id],
    queryFn: () => api.get(`/software/user/${user.id}`).then(r => r.data),
  })

  const assets = data?.assets || []
  const totalItems = assets.length + licenses.length

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Monitor size={16}/> สินทรัพย์ที่ผูกกับผู้ใช้</h2>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                {(user.displayName||user.username)[0].toUpperCase()}
              </div>
              <span className="text-sm text-gray-600">{user.displayName || user.username}</span>
              {user.employeeId && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{user.employeeId}</span>}
              {!isLoading && !licLoading && <span className="text-xs text-gray-400">· {totalItems} รายการ</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {(isLoading || licLoading) && (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <RefreshCw size={20} className="animate-spin mr-2"/> กำลังโหลด...
            </div>
          )}
          {!isLoading && !licLoading && totalItems === 0 && (
            <div className="flex flex-col items-center py-10 text-gray-400 gap-2">
              <Monitor size={32} className="text-gray-200"/>
              <p className="text-sm">ไม่พบสินทรัพย์ที่ผูกกับผู้ใช้นี้</p>
              <p className="text-xs text-gray-300">ค้นหาจากชื่อ "{user.displayName||user.username}"{user.employeeId ? ` และรหัส ${user.employeeId}` : ''}</p>
            </div>
          )}
          {!isLoading && assets.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Monitor size={11}/> Hardware ({assets.length})
              </p>
              <div className="space-y-2">
                {assets.map(asset => (
                  <div key={asset.id} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <Monitor size={16} className="text-gray-500"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {asset.assetCode && <span className="text-xs font-mono text-blue-600 font-semibold">{asset.assetCode}</span>}
                          <span className="text-sm font-medium text-gray-900 truncate">{asset.name}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {[asset.brand, asset.model].filter(Boolean).join(' · ')}
                          {asset.company ? ` · ${asset.company}` : ''}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_BADGE[asset.status]||'bg-gray-100 text-gray-500'}`}>
                        {STATUS_TH[asset.status]||asset.status}
                      </span>
                      <button onClick={() => setExpandedId(expandedId===asset.id ? null : asset.id)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0" title="Software / Components">
                        {expandedId===asset.id ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </button>
                      <a href={`/assets/${asset.id}`} target="_blank" rel="noreferrer"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0" title="ดูรายละเอียด">
                        <ExternalLink size={14}/>
                      </a>
                    </div>
                    {expandedId === asset.id && (
                      <div className="px-3 pb-3">
                        <AssetDetails assetId={asset.id}/>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!licLoading && licenses.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <KeyRound size={11}/> Software & License ({licenses.length})
              </p>
              <div className="space-y-2">
                {licenses.map((lic: any) => (
                  <div key={lic.assignmentId || lic.id} className="bg-violet-50 rounded-xl border border-violet-100 flex items-center gap-3 p-3">
                    <div className="w-9 h-9 rounded-xl bg-white border border-violet-200 flex items-center justify-center flex-shrink-0">
                      <KeyRound size={16} className="text-violet-500"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${lic.softwareCategory==='OS'?'bg-indigo-200 text-indigo-700':'bg-gray-200 text-gray-600'}`}>
                          {lic.softwareCategory==='OS'?'OS':'App'}
                        </span>
                        <span className="text-sm font-medium text-gray-900 truncate">{lic.name}</span>
                        {lic.version && <span className="text-xs text-gray-400 flex-shrink-0">v{lic.version}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {lic.company?.name && <span className="text-xs text-gray-400">{lic.company.name}</span>}
                        {lic.expiryDate && (
                          <span className={`text-xs ${new Date(lic.expiryDate) < new Date() ? 'text-red-500' : 'text-gray-400'}`}>
                            หมดอายุ {new Date(lic.expiryDate).toLocaleDateString('th-TH')}
                          </span>
                        )}
                        {lic.assignedAt && (
                          <span className="text-xs text-gray-300">ผูกเมื่อ {new Date(lic.assignedAt).toLocaleDateString('th-TH')}</span>
                        )}
                      </div>
                    </div>
                    <a href={`/software`} target="_blank" rel="noreferrer"
                      className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-100 rounded-lg transition-colors flex-shrink-0" title="ดูรายละเอียด">
                      <ExternalLink size={14}/>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">ปิด</button>
        </div>
      </div>
    </div>
  )
}

interface CsvRow { username?:string; displayName?:string; email?:string; role?:string; employeeId?:string; companyId?:string }

function ImportUsersModal({ companies, onClose }: { companies: Company[]; onClose: () => void }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<CsvRow[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [importing, setImporting] = useState(false)
  const [done, setDone] = useState<{created:number;skipped:number}|null>(null)

  const TEMPLATE = 'username,displayName,email,role,employeeId,companyId\njohn.doe,John Doe,john@company.com,VIEWER,EMP001,\n'

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type:'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'users_template.csv'; a.click()
  }

  const handleFile = (file: File|null) => {
    if (!file) return
    Papa.parse<CsvRow>(file, {
      header: true, skipEmptyLines: true,
      transformHeader: h => h.trim().toLowerCase().replace(/\s+/g,''),
      complete: ({ data }) => {
        const errs: string[] = []
        const cleaned = data.map((r:any, i:number) => {
          const username = (r.username||r['username']||'').trim()
          if (!username) errs.push(`แถว ${i+2}: ไม่มี username`)
          const role = (r.role||'VIEWER').trim().toUpperCase()
          if (!['ADMIN','VIEWER','TECHNICIAN'].includes(role)) errs.push(`แถว ${i+2}: role "${role}" ไม่ถูกต้อง`)
          return { username, displayName:(r.displayname||r.displayName||'').trim()||undefined, email:(r.email||'').trim()||undefined, role, employeeId:(r.employeeid||r.employeeId||'').trim()||undefined, companyId:(r.companyid||r.companyId||'').trim()||undefined }
        })
        setErrors(errs)
        setRows(cleaned)
      }
    })
  }

  const handleImport = async () => {
    if (rows.length === 0 || errors.length > 0) return
    setImporting(true)
    let created = 0; let skipped = 0
    for (const row of rows) {
      try {
        await api.post('/users', { ...row, password: 'ChangeMe@123' })
        created++
      } catch { skipped++ }
    }
    qc.invalidateQueries({ queryKey: ['users'] })
    setDone({ created, skipped })
    setImporting(false)
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Upload size={16}/> Import ผู้ใช้งานจาก CSV</h2>
            <p className="text-xs text-gray-400 mt-0.5">รหัสผ่านเริ่มต้นของทุก user จะเป็น <code className="bg-gray-100 px-1 rounded">ChangeMe@123</code></p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Template download */}
          <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
            <Download size={16} className="text-blue-600 mt-0.5 flex-shrink-0"/>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">คอลัมน์ที่รองรับ</p>
              <p className="text-xs text-blue-600 mt-0.5 font-mono">username, displayName, email, role, employeeId, companyId</p>
              <p className="text-xs text-blue-500 mt-1">role: ADMIN / VIEWER / TECHNICIAN (ค่าเริ่มต้น VIEWER)</p>
            </div>
            <button onClick={downloadTemplate} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 flex-shrink-0">
              <Download size={12}/> ดาวน์โหลด Template
            </button>
          </div>

          {/* Upload area */}
          <div onClick={()=>fileRef.current?.click()}
            className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors">
            <Upload size={24} className="mx-auto text-gray-400 mb-2"/>
            <p className="text-sm font-medium text-gray-600">คลิกเพื่อเลือกไฟล์ CSV</p>
            <p className="text-xs text-gray-400 mt-1">รองรับ .csv เท่านั้น</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e=>handleFile(e.target.files?.[0]||null)}/>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 rounded-xl p-4 space-y-1">
              <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><AlertCircle size={14}/> พบข้อผิดพลาด</p>
              {errors.map((e,i)=><p key={i} className="text-xs text-red-600">{e}</p>)}
            </div>
          )}

          {/* Preview */}
          {rows.length > 0 && errors.length === 0 && !done && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">พบข้อมูล {rows.length} รายการ</p>
              <div className="border border-gray-200 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>{['username','displayName','email','role','employeeId','companyId'].map(h=><th key={h} className="text-left px-3 py-2 font-medium text-gray-500">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((r,i)=>(
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 font-medium">{r.username}</td>
                        <td className="px-3 py-1.5 text-gray-600">{r.displayName||'—'}</td>
                        <td className="px-3 py-1.5 text-gray-500">{r.email||'—'}</td>
                        <td className="px-3 py-1.5"><span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{r.role}</span></td>
                        <td className="px-3 py-1.5 text-gray-500">{r.employeeId||'—'}</td>
                        <td className="px-3 py-1.5 text-gray-500">{r.companyId||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Done */}
          {done && (
            <div className="bg-green-50 rounded-xl p-5 text-center">
              <Check size={32} className="mx-auto text-green-500 mb-2"/>
              <p className="font-semibold text-green-800">Import เสร็จสิ้น</p>
              <p className="text-sm text-green-600 mt-1">สร้างสำเร็จ {done.created} คน · ข้ามไปแล้ว {done.skipped} คน (username ซ้ำ)</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">
            {done ? 'ปิด' : 'ยกเลิก'}
          </button>
          {!done && (
            <button onClick={handleImport}
              disabled={rows.length === 0 || errors.length > 0 || importing}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {importing ? <RefreshCw size={14} className="animate-spin"/> : <Upload size={14}/>}
              {importing ? 'กำลัง Import...' : `Import (${rows.length} รายการ)`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function UsersPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const currentUser = (() => { try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null } catch { return null } })()
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN'
  const [showImportCsv, setShowImportCsv] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [assetsUser, setAssetsUser] = useState<User | null>(null)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [showO365, setShowO365] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filterCompany, setFilterCompany] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const { data: usersData, isLoading } = useQuery<{ users: User[]; total: number }>({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r => r.data),
  })
  const users: User[] = usersData?.users || []
  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['masterdata', 'companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: (d: any) => api.post('/users', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setShowForm(false) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, ...d }: any) => api.put(`/users/${id}`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setEditUser(null) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setDeleteId(null) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })
  const bulkDeleteMut = useMutation({
    mutationFn: (ids: string[]) => api.delete('/users/bulk', { data: { ids } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setSelectedIds(new Set()) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.department?.toLowerCase().includes(search.toLowerCase()) ||
      u.position?.toLowerCase().includes(search.toLowerCase())
    const matchCompany = !filterCompany || u.companyId === filterCompany
    const matchRole    = !filterRole    || u.role === filterRole
    return matchSearch && matchCompany && matchRole
  })

  const filteredIds = filtered.map(u => u.id)
  const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.has(id))
  const someSelected = filteredIds.some(id => selectedIds.has(id))
  const toggleAll = () => {
    if (allSelected) setSelectedIds(prev => { const s = new Set(prev); filteredIds.forEach(id => s.delete(id)); return s })
    else setSelectedIds(prev => new Set([...prev, ...filteredIds]))
  }
  const toggleOne = (id: string) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })
  const handleBulkDelete = () => {
    if (!confirm(`ลบ ${selectedIds.size} ผู้ใช้ที่เลือก?`)) return
    bulkDeleteMut.mutate([...selectedIds])
  }

  return (
    <div className="h-full flex flex-col gap-4 p-6 max-w-5xl mx-auto overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Users size={22}/> จัดการผู้ใช้งาน</h1>
          <p className="text-sm text-gray-500 mt-0.5">{users.length} ผู้ใช้ทั้งหมด</p>
        </div>
        <div className="flex gap-2 items-center">
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} disabled={bulkDeleteMut.isPending}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50">
              <Trash2 size={14}/> ลบที่เลือก ({selectedIds.size})
            </button>
          )}
          <button onClick={() => setShowO365(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700">
            <Cloud size={15}/> นำเข้าจาก O365
          </button>
          <button onClick={() => setShowImportCsv(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">
            <Upload size={15}/> Import CSV
          </button>
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            <Plus size={15}/> เพิ่มผู้ใช้
          </button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex gap-3 flex-shrink-0">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            placeholder="ค้นหา username, ชื่อ, email..."/>
        </div>
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          <option value="">ทุก Role</option>
          <option value="SUPER_ADMIN">Super Admin</option>
          <option value="ADMIN">Admin</option>
          <option value="TECHNICIAN">Technician</option>
          <option value="VIEWER">Viewer</option>
        </select>
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          <option value="">ทุกบริษัท</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-y-auto flex-1 min-h-0">
        <table className="w-full text-sm table-fixed">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-3 py-3 w-10">
                <input type="checkbox" checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected }}
                  onChange={toggleAll} className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"/>
              </th>
              <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs w-[22%]">ผู้ใช้</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs w-[15%]">Email</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs w-[13%]">แผนก / ตำแหน่ง</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs w-[12%]">บริษัท</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs w-[10%]">สิทธิ์</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs w-[9%]">สถานะ</th>
              <th className="text-left px-3 py-3 font-medium text-gray-500 text-xs w-[10%]">แหล่งที่มา</th>
              <th className="px-3 py-3 w-[13%]"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading && <tr><td colSpan={9} className="text-center py-10 text-gray-400">กำลังโหลด...</td></tr>}
            {!isLoading && filtered.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-gray-400">ไม่มีผู้ใช้</td></tr>}
            {filtered.map(u => {
              const RoleIcon = ROLE_ICONS[u.role]
              return (
                <tr key={u.id} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(u.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-2.5 w-10" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleOne(u.id)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"/>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        u.isActive ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {(u.displayName || u.username)[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{u.displayName || u.username}</p>
                        <p className="text-xs text-gray-400 truncate">@{u.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs overflow-hidden">
                    <span className="truncate block">{u.email || '—'}</span>
                  </td>
                  <td className="px-3 py-2.5 overflow-hidden">
                    {(u.department || u.position) ? (
                      <div className="min-w-0">
                        {u.department && <p className="text-xs text-gray-700 truncate">{u.department}</p>}
                        {u.position && <p className="text-xs text-gray-400 truncate">{u.position}</p>}
                      </div>
                    ) : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 overflow-hidden">
                    {u.company
                      ? <span className="flex items-center gap-1 text-xs text-gray-700"><Building2 size={11} className="text-gray-400 flex-shrink-0"/><span className="truncate min-w-0">{u.company.name}</span></span>
                      : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${ROLE_COLORS[u.role]}`}>
                      <RoleIcon size={10}/> {ROLE_LABELS[u.role]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      u.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>{u.isActive ? 'เปิดใช้' : 'ปิดใช้'}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    {u.o365Id
                      ? <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full whitespace-nowrap"><Cloud size={10}/> O365</span>
                      : <span className="text-xs text-gray-400">Local</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => navigate(`/users/${u.id}`)} className="p-1.5 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors" title="ดู Profile"><ExternalLink size={14}/></button>
                      <button onClick={() => setAssetsUser(u)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="ดูสินทรัพย์ (quick)"><Monitor size={14}/></button>
                      <button onClick={() => setEditUser(u)} className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="แก้ไข"><Edit2 size={14}/></button>
                      <button onClick={() => setDeleteId(u.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="ลบ"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Modals */}
      {assetsUser && <UserAssetsModal user={assetsUser} onClose={() => setAssetsUser(null)}/>}
      {showImportCsv && <ImportUsersModal companies={companies} onClose={() => setShowImportCsv(false)}/>}
      {showForm && <UserFormModal companies={companies} isSuperAdmin={isSuperAdmin} onClose={() => setShowForm(false)} onSave={d => createMut.mutate(d)}/>}
      {editUser && <UserFormModal user={editUser} companies={companies} isSuperAdmin={isSuperAdmin} onClose={() => setEditUser(null)} onSave={d => updateMut.mutate({ id: editUser.id, ...d })}/>}
      {showO365 && <O365ImportModal companies={companies} onClose={() => setShowO365(false)}/>}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Trash2 size={20} className="text-red-600"/>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">ยืนยันการลบ</h3>
            <p className="text-sm text-gray-500 mb-5">การลบผู้ใช้จะไม่สามารถกู้คืนได้</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">ยกเลิก</button>
              <button onClick={() => deleteMut.mutate(deleteId!)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 flex items-center justify-center gap-1.5">
                {deleteMut.isPending ? <RefreshCw size={14} className="animate-spin"/> : <Check size={14}/>} ลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
